'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

export interface GiftInfo {
  isGift: boolean;
  recipientEmail: string;
  message: string;
}

export interface CartItemLicensingProps {
  itemId: string;
  templateTitle: string;
  quantity: number;
  giftInfo: GiftInfo;
  onQuantityChange: (itemId: string, qty: number) => void;
  onGiftChange: (itemId: string, gift: GiftInfo) => void;
}

export default function CartItemLicensing({
  itemId,
  templateTitle,
  quantity,
  giftInfo,
  onQuantityChange,
  onGiftChange,
}: CartItemLicensingProps) {
  const [emailError, setEmailError] = useState('');

  const handleQuantityChange = (delta: number) => {
    const next = Math.max(1, Math.min(20, quantity + delta));
    onQuantityChange(itemId, next);
  };

  const handleGiftToggle = (checked: boolean) => {
    onGiftChange(itemId, { ...giftInfo, isGift: checked });
    if (!checked) setEmailError('');
  };

  const handleEmailChange = (email: string) => {
    setEmailError('');
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Please enter a valid email address.');
    }
    onGiftChange(itemId, { ...giftInfo, recipientEmail: email });
  };

  return (
    <div
      className="mt-3 rounded-xl p-3 space-y-3"
      style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}
    >
      {/* License Quantity */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Icon name="KeyIcon" size={14} style={{ color: 'var(--accent)' } as React.CSSProperties} />
          <div>
            <p className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
              License Quantity
            </p>
            <p className="text-2xs" style={{ color: 'var(--text-muted)' }}>
              {quantity} activation {quantity === 1 ? 'key' : 'keys'} will be generated
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => handleQuantityChange(-1)}
            disabled={quantity <= 1}
            className="w-7 h-7 rounded-lg flex items-center justify-center transition-all"
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              color: quantity <= 1 ? 'var(--text-muted)' : 'var(--text-primary)',
              opacity: quantity <= 1 ? 0.5 : 1,
            }}
          >
            <Icon name="MinusIcon" size={12} />
          </button>
          <span
            className="w-8 text-center text-sm font-semibold"
            style={{ color: 'var(--text-primary)' }}
          >
            {quantity}
          </span>
          <button
            type="button"
            onClick={() => handleQuantityChange(1)}
            disabled={quantity >= 20}
            className="w-7 h-7 rounded-lg flex items-center justify-center transition-all"
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              color: quantity >= 20 ? 'var(--text-muted)' : 'var(--text-primary)',
              opacity: quantity >= 20 ? 0.5 : 1,
            }}
          >
            <Icon name="PlusIcon" size={12} />
          </button>
        </div>
      </div>

      {/* Gift Toggle */}
      <div
        className="pt-3"
        style={{ borderTop: '1px solid var(--border)' }}
      >
        <label className="flex items-center gap-2.5 cursor-pointer">
          <div className="relative flex-shrink-0">
            <input
              type="checkbox"
              checked={giftInfo.isGift}
              onChange={(e) => handleGiftToggle(e.target.checked)}
              className="sr-only"
            />
            <div
              className="w-5 h-5 rounded flex items-center justify-center transition-all"
              style={{
                background: giftInfo.isGift ? 'var(--accent)' : 'var(--bg-card)',
                border: `2px solid ${giftInfo.isGift ? 'var(--accent)' : 'var(--border)'}`,
              }}
            >
              {giftInfo.isGift && (
                <Icon name="CheckIcon" size={11} style={{ color: '#fff' } as React.CSSProperties} />
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <Icon name="GiftIcon" size={14} style={{ color: 'var(--accent)' } as React.CSSProperties} />
            <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
              Gift &ldquo;{templateTitle}&rdquo; to someone
            </span>
          </div>
        </label>

        {giftInfo.isGift && (
          <div className="mt-3 space-y-2.5">
            <div>
              <label
                className="block text-xs font-semibold mb-1"
                style={{ color: 'var(--text-secondary)' }}
              >
                Recipient Email *
              </label>
              <input
                type="email"
                placeholder="recipient@example.com"
                value={giftInfo.recipientEmail}
                onChange={(e) => handleEmailChange(e.target.value)}
                className="input-field text-xs py-2"
              />
              {emailError && (
                <p className="text-xs mt-1" style={{ color: '#EF4444' }}>
                  {emailError}
                </p>
              )}
              <p className="text-2xs mt-1" style={{ color: 'var(--text-muted)' }}>
                The recipient must create an account with this email to claim the key.
              </p>
            </div>
            <div>
              <label
                className="block text-xs font-semibold mb-1"
                style={{ color: 'var(--text-secondary)' }}
              >
                Personal Message (optional)
              </label>
              <textarea
                placeholder="Add a personal message..."
                value={giftInfo.message}
                onChange={(e) =>
                  onGiftChange(itemId, { ...giftInfo, message: e.target.value })
                }
                rows={2}
                className="input-field text-xs py-2 resize-none"
              />
            </div>
            <div
              className="flex items-start gap-2 p-2.5 rounded-lg text-xs"
              style={{ background: '#FEF3C718', border: '1px solid #F59E0B40', color: '#92400E' }}
            >
              <Icon
                name="InformationCircleIcon"
                size={13}
                className="flex-shrink-0 mt-0.5"
                style={{ color: '#F59E0B' } as React.CSSProperties}
              />
              <span style={{ color: 'var(--text-secondary)' }}>
                You will <strong>not</strong> have access to this gifted key. The recipient must
                sign up with the email above to claim it.
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
